import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { filenameFromContentDisposition } from '../shared/file-download';
import {
  ReleaseReportPreview,
  ReleaseReportRequest,
  ReportArt,
  ReportDownload,
  ReportPreviewScope,
  ReportVersion,
} from '../models/release-report.model';

const UNSAFE_FILENAME_CHARS = /[^A-Za-z0-9._-]/g;
const REPORT_FILE_NAME: Record<ReportArt, string> = {
  STATUS: 'Statusbericht',
  ABSCHLUSS: 'Abschlussbericht',
  TRACEABILITY: 'Traceability',
};

@Injectable({ providedIn: 'root' })
export class ReleaseReportService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/projects`;

  getPreview(
    projectId: string,
    releaseId: string,
    art: ReportArt,
    planId: string | null,
    scope: ReportPreviewScope = {},
  ): Observable<ReleaseReportPreview> {
    let params = new HttpParams().set('art', art);
    if (planId) params = params.set('planId', planId);
    if (scope.zeitraum) params = params.set('zeitraum', scope.zeitraum);
    if (scope.teststufe) params = params.set('teststufe', scope.teststufe);
    return this.http.get<ReleaseReportPreview>(`${this.reportsUrl(projectId, releaseId)}/preview`, { params });
  }

  /** `planKey` geht nur in den Dateinamen ein, wenn der Server keinen lesbaren nennt. */
  generate(
    projectId: string,
    releaseId: string,
    request: ReleaseReportRequest,
    planKey: string | null,
  ): Observable<ReportDownload> {
    return this.http
      .post(this.reportsUrl(projectId, releaseId), request, { responseType: 'blob', observe: 'response' })
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename: filenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            fallbackFilename(request, releaseId, planKey),
          ),
        })),
        catchError((err: unknown) => from(readBlobError(err)).pipe(switchMap((e) => throwError(() => e)))),
      );
  }

  listVersions(projectId: string, releaseId: string): Observable<ReportVersion[]> {
    return this.http.get<ReportVersion[]>(`${this.reportsUrl(projectId, releaseId)}/versions`);
  }

  downloadVersion(projectId: string, releaseId: string, version: ReportVersion): Observable<ReportDownload> {
    return this.http
      .get(`${this.reportsUrl(projectId, releaseId)}/versions/${version.version}/file`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename: filenameFromContentDisposition(response.headers.get('Content-Disposition'), version.dateiname),
        })),
        catchError((err: unknown) => from(readBlobError(err)).pipe(switchMap((e) => throwError(() => e)))),
      );
  }

  private reportsUrl(projectId: string, releaseId: string): string {
    return `${this.base}/${projectId}/releases/${encodeURIComponent(releaseId)}/reports`;
  }
}

function fallbackFilename(request: ReleaseReportRequest, releaseId: string, planKey: string | null): string {
  const today = new Date();
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const parts = [REPORT_FILE_NAME[request.art], releaseId, ...(planKey ? [planKey] : []), date];
  const name = parts.join('_').replace(UNSAFE_FILENAME_CHARS, '_');
  return `${name}.${request.format.toLowerCase()}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// Mit responseType blob kommt auch der Fehler-Body als Blob; erst gelesen liefert er `error` und `code`.
async function readBlobError(err: unknown): Promise<unknown> {
  if (!(err instanceof HttpErrorResponse) || !(err.error instanceof Blob)) return err;
  let body: unknown;
  try {
    body = JSON.parse(await err.error.text());
  } catch {
    body = null;
  }
  return new HttpErrorResponse({
    error: body,
    headers: err.headers,
    status: err.status,
    statusText: err.statusText,
    url: err.url ?? undefined,
  });
}
